import { useState, useEffect } from 'react';
import cn from 'classnames';
import addCheckoutAttributes from 'lib/shopify/addCheckoutAttributes';
import SiteLink from 'components/shared/sitelink';
import { useSettings } from 'contexts/SettingContext';
import useIsMobile from 'hooks/utils/useIsMobile';
import { addLocaleToHref } from 'lib/utils/helpers';
import useViewCartTracking from 'hooks/tracking/useViewCartTracking';
import { useCart, useCustomer, useMultipass } from 'hooks';
import { useRouter } from 'next/router';
import {
  setSessionCheckoutUrl,
  getSelectedSalonNo,
  getStorageCustomerSalonNo,
  formatPriceByStoreLocale,
  getCheckoutIdByLocale,
} from 'lib/shopify';
import LockIcon from 'components/shared/lockIcon/lockIcon';
import BuyingPros from '../../shared/buyingPros';
import PaymentIcons from '../../shared/paymentIcons';
import LineItem from './lineItem';
import SalonsList from '../../salonsList';
import styles from './cart.module.scss';
import CartWishlist from './cartWishlist';
import OutOfStockPopup from './outOfStockPopup/outOfStockPopup';

const Cart = ({ locale, globalModules }) => {
  const { trackedItemsSelectList } = useSettings();
  const { cart, isEmpty } = useCart();
  const [empty, setEmpty] = useState(isEmpty);
  const { customer } = useCustomer();
  const isMobile = useIsMobile();
  const {
    btncolors,
    checkout,
    cart: cartModules,
    settings,
    secondpartsettings,
    shipping,
  } = globalModules;
  const [contentfulData, setContentfulData] = useState([]);
  const [isCartViewed, setIsCartViewed] = useState(false);
  const [outOfStocLineItems, setOutOfStocLineItems] = useState(null);
  const router = useRouter();
  const hasSalonSelector =
    settings?.localesWithWebshop?.onOrOff && secondpartsettings?.localesWithSalonSelector?.onOrOff;
  const hasShippingCost = shipping.localeHasShippingBar.onOrOff !== false;

  const subTotalPrice = cart?.cost?.subtotalAmount?.amount;
  const totalPrice = cart?.cost?.totalAmount?.amount;
  const totalPriceIsValid = totalPrice && totalPrice !== subTotalPrice;

  useEffect(() => {
    document.body.style.paddingBottom = isMobile && !isEmpty ? '11rem' : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isMobile, isEmpty]);

  useEffect(() => {
    const fetchProducts = async () => {
      const productSlugs = cart.lines?.edges
        .map(({ node }) => node.merchandise?.product?.handle)
        .filter(Boolean);

      const products = await fetch('/api/contentful/get-products', {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          productSlugs,
          locale,
        }),
      });

      try {
        if (products.ok) {
          const productsWithContentfulData = await products.json();
          setContentfulData(productsWithContentfulData);
        }
        return null;
      } catch (e) {
        return products.status(500).json('There seems to have been an issue:', e);
      }
    };

    if (cart.lines?.edges?.length) {
      fetchProducts();
    }
  }, [cart.lines?.edges?.length]);

  useEffect(() => {
    if (contentfulData?.length && !isCartViewed) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useViewCartTracking({
        currencyCode: cart.currencyCode,
        lineItems: cart.lines?.edges,
        contentfulData,
        trackedItemsSelectList,
      });
      setIsCartViewed(true);
    }
  }, [contentfulData]);

  const { cart: localizedTexts } = globalModules;
  const buyingProsInfo = settings.buyingPros.sections;

  const t = key => localizedTexts[key]?.text;

  const startCheckout = async () => {
    const checkoutId = getCheckoutIdByLocale(locale);
    const affiliatedSalonNo = getStorageCustomerSalonNo(locale);
    const selectedSalonNo = getSelectedSalonNo(locale);
    const attributes =
      affiliatedSalonNo || selectedSalonNo
        ? [
            {
              key: 'salonNo',
              value:
                (Boolean(selectedSalonNo) && selectedSalonNo !== 'null' ? selectedSalonNo : null) ||
                affiliatedSalonNo,
            },
            {
              key: 'affiliatedSalonNo',
              value: affiliatedSalonNo || '',
            },
          ]
        : null;

    if (attributes) await addCheckoutAttributes(checkoutId, attributes, locale);

    if (customer?.isLoggedIn) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const url = await useMultipass(customer.email, cart.webUrl, locale);
      document.location.href = url;
    } else {
      // We save the checkout url so that we can redirect to it after login
      setSessionCheckoutUrl(locale, cart.webUrl);
      document.location.href = cart.webUrl;
    }
  };

  const handleCheckout = () => {
    const outOfStockItems = cart.lines?.edges?.filter(({ node }) =>
      node?.merchandise?.product.tags.includes('out-of-stock')
    );

    if (outOfStockItems.length) {
      setOutOfStocLineItems(outOfStockItems);
    } else {
      startCheckout();
    }
  };

  useEffect(() => {
    setEmpty(isEmpty);
  }, [isEmpty]);

  return (
    <>
      <section className={styles.cart}>
        <div className={styles.cartHeader}>
          <h1>{t('yourCart')}</h1>
        </div>
        {!empty ? (
          <div className={cn(styles.productsList, isMobile && styles.topSpacing)}>
            <div className={styles.leftSide}>
              {!empty && (
                <div className={styles.productsHeader}>
                  <span className={styles.firstColumn}>{cartModules.product.text}</span>
                  <span className={styles.fillerColumn}>FILLER</span>
                  <span className={styles.secondColumn}>{cartModules.price.text}</span>
                  <span className={styles.thirdColumn}>{cartModules.quantity.text}</span>
                </div>
              )}
              {!empty && isMobile && hasSalonSelector && (
                <SalonsList globalModules={globalModules} locale={locale} />
              )}
              {!empty && (
                <div className={styles.lineItemsSection}>
                  {cart?.lines?.edges?.map(({ node }, i) => (
                    <LineItem
                      locale={locale}
                      lineItem={node}
                      key={node.id}
                      index={i}
                      cart={cart}
                      globalModules={globalModules}
                      contentfulData={contentfulData?.find(
                        c => c?.slug === node?.merchandise?.product?.handle
                      )}
                    />
                  ))}
                </div>
              )}

              {isMobile && <PaymentIcons className={styles.paymentMethods} locale={locale} />}

              {!empty && isMobile && buyingProsInfo.length > 0 && (
                <div className={styles.mobileBuyingPros}>
                  <BuyingPros
                    data={buyingProsInfo[0]}
                    className={cn({
                      [styles.cartAdjustment]: !isMobile,
                      [styles.centerElement]: isMobile,
                    })}
                    page="cart"
                  />
                </div>
              )}
            </div>
            <div
              className={cn(styles.rightSide, {
                [styles.rightSideMarginTop]: !hasSalonSelector,
              })}
            >
              {!empty && !isMobile && hasSalonSelector && (
                <SalonsList globalModules={globalModules} locale={locale} />
              )}
              {!empty && !isMobile && (
                <div className={styles.subContainer}>
                  <div className={cn(styles.subtotal, isMobile && styles.subtotalSpecs)}>
                    <span>{cartModules.subtotal.text}</span>
                    <div className={styles.totalContainer}>
                      <span
                        className={cn({
                          [styles.lineThroughText]: !isEmpty && totalPriceIsValid,
                        })}
                      >
                        {formatPriceByStoreLocale({
                          locale,
                          price: Number(subTotalPrice).toFixed(2),
                        })}
                      </span>
                      {!isEmpty && totalPriceIsValid && (
                        <span className={styles.salesPrice}>
                          {formatPriceByStoreLocale({
                            locale,
                            price: Number(totalPrice).toFixed(2),
                          })}
                        </span>
                      )}
                    </div>
                    {hasShippingCost &&
                    cart?.totalPriceV2?.amount > parseInt(shipping.freeShippingPrice.text, 10) ? (
                      <span className={styles.shipping}>{checkout.freeShipping.text}</span>
                    ) : (
                      <span className={styles.shipping}>{checkout.shippingCostsMessage.text}</span>
                    )}
                    {!isMobile && (
                      <button
                        className={styles.checkout}
                        style={{
                          backgroundColor: btncolors?.checkoutButtonColor.text || '',
                          color: btncolors?.checkoutButtonTextColor.text || '',
                        }}
                        type="button"
                        onClick={() => handleCheckout()}
                      >
                        <LockIcon
                          color={btncolors?.checkoutButtonTextColor.text}
                          width="2rem"
                          height="2rem"
                        />
                        <span className={styles.checkoutContent}>{settings.checkout.text}</span>
                      </button>
                    )}
                  </div>
                  <PaymentIcons className={styles.paymentMethods} locale={locale} />
                  {buyingProsInfo.length > 0 && (
                    <BuyingPros
                      data={buyingProsInfo[0]}
                      className={cn({
                        [styles.cartAdjustment]: !isMobile,
                        [styles.centerElement]: isMobile,
                      })}
                      page="cart"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.emptyContainer}>
            <div className={styles.notification}>{t('emptyCart')}</div>
            <SiteLink
              locale={locale}
              href={addLocaleToHref(settings?.slugEmptyCartButton?.link?.slug, locale, router)}
            >
              <button className={styles.checkoutButton} type="button">
                {t('startShopping')}
              </button>
            </SiteLink>
          </div>
        )}
      </section>
      {!empty && isMobile && (
        <section className={styles.checkoutSection}>
          <div className={styles.subContainer}>
            <div className={cn(styles.subtotal, isMobile && styles.subtotalSpecs)}>
              <span>{cartModules.subtotal.text}</span>
              <div className={styles.totalContainer}>
                <span
                  className={cn({
                    [styles.lineThroughText]: !isEmpty && totalPriceIsValid,
                  })}
                >
                  {formatPriceByStoreLocale({
                    locale,
                    price: Number(subTotalPrice).toFixed(2),
                  })}
                </span>
                {!isEmpty && totalPriceIsValid && (
                  <span className={styles.salesPrice}>
                    {formatPriceByStoreLocale({
                      locale,
                      price: Number(totalPrice).toFixed(2),
                    })}
                  </span>
                )}
              </div>
              {hasShippingCost &&
              cart?.totalPriceV2?.amount > parseInt(shipping.freeShippingPrice.text, 10) ? (
                <span className={styles.shipping}>{checkout.freeShipping.text}</span>
              ) : (
                <span className={styles.shipping}>{checkout.shippingCostsMessage.text}</span>
              )}
            </div>
            <button
              className={styles.checkout}
              style={{
                backgroundColor: btncolors?.checkoutButtonColor.text || '',
                color: btncolors?.checkoutButtonTextColor.text || '',
              }}
              type="button"
              onClick={() => handleCheckout()}
            >
              <LockIcon
                color={btncolors?.checkoutButtonTextColor.text}
                width="2rem"
                height="2rem"
              />
              <span className={styles.checkoutContent}>{settings.checkout.text}</span>
            </button>
          </div>
        </section>
      )}
      <CartWishlist globalModules={globalModules} locale={locale} cart={cart} />
      {outOfStocLineItems?.length && (
        <OutOfStockPopup
          globalModules={globalModules}
          lineItems={outOfStocLineItems}
          closeModal={() => {
            setOutOfStocLineItems(null);
          }}
          startCheckout={startCheckout}
          locale={locale}
          cart={cart}
        />
      )}
    </>
  );
};

export default Cart;
